import React, { useState } from 'react';
import api from '../services/api';
import './NabersImportModal.css';

// Numeric formatting groups for the change-diff views (kept in sync with the
// backend's shared nabers-fields module)
const GWP_DIFF_FIELDS = [
    'gwp_total_kg_co2e_quant',
    'gwp_fossil_kg_co2e_quant',
    'gwp_biogenic_kg_co2e_quant',
    'gwp_luluc_kg_co2e_quant',
    'gwp_stored_kg_co2e_quant',
    'declared_gwp_total',
    'declared_gwp_fossil',
    'declared_gwp_biogenic',
    'declared_gwp_luluc',
    'declared_gwp_stored'
];

const NUMERIC_DIFF_FIELDS = [
    'density_kg_m3', 'area_density_kg_m2', 'mass_per_m_kg_m',
    'mass_per_unit_kg_unit', ...GWP_DIFF_FIELDS
];

// Mirrors routes/nabers-linkgen.js — the property that gives the mass of one
// nezo unit, and the flag recording that the figure was supplied by hand.
const NEZO_UNIT_OPTIONS = ['m³', 'm²', 'm', 'kg', '#'];
const MASS_FIELD_FOR_UNIT = {
    'm³': 'density_kg_m3',
    'm²': 'area_density_kg_m2',
    'm': 'mass_per_m_kg_m',
    '#': 'mass_per_unit_kg_unit',
    'kg': null,          // 1 kg per kg — nothing to look up
};
const ESTIMATED_FLAG_FOR = {
    density_kg_m3: 'density_is_estimated',
    area_density_kg_m2: 'area_density_is_estimated',
    mass_per_m_kg_m: 'mass_per_m_is_estimated',
    mass_per_unit_kg_unit: 'mass_per_unit_is_estimated',
};
const MASS_LABEL_FOR_UNIT = {
    'm³': 'density (kg/m³)',
    'm²': 'area density (kg/m²)',
    'm': 'mass per m (kg/m)',
    '#': 'mass per unit (kg/unit)',
};

const normaliseUnitClient = (raw) => {
    if (raw === null || raw === undefined) return '';
    const u = String(raw).trim();
    if (!u || u.toUpperCase() === 'NULL') return '';
    if (u.toLowerCase() === 'm3') return 'm³';
    if (u.toLowerCase() === 'm2') return 'm²';
    return u;
};

const NabersImportModal = ({ onClose, onImportComplete, epdCategories = [], materialTypeGroups = [] }) => {
    const [file, setFile] = useState(null);
    // Import scope: EF rows only, or also create the MUR / Product material chain.
    // Linkage is always create-only — an existing MUR or QU is never modified.
    const [mode, setMode] = useState('epd');
    const [categoryId, setCategoryId] = useState('');
    const [quCategoryId, setQuCategoryId] = useState('');
    const [quColorHex, setQuColorHex] = useState('#000000');
    const [includeZeroWeight, setIncludeZeroWeight] = useState(false);
    const [linkSummary, setLinkSummary] = useState(null);
    const [linkPlans, setLinkPlans] = useState(null);
    // Nezo unit resolution: a fresh EPD only carries its declared unit, so the
    // target has to be chosen per (material type + declared unit) group.
    const [unitGroups, setUnitGroups] = useState(null);
    const [unitChoices, setUnitChoices] = useState({});     // groupKey -> nezo unit
    const [groupNotes, setGroupNotes] = useState({});       // groupKey -> source note
    const [typedProps, setTypedProps] = useState({});       // efId -> value typed by hand
    const [keepAsDeclared, setKeepAsDeclared] = useState({}); // groupKey -> bool
    const [suggestions, setSuggestions] = useState({});     // materialType -> {unit, share, sampleSize}
    const [analyzing, setAnalyzing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [expandedRows, setExpandedRows] = useState(new Set());

    // ✅ Field selection state for updates
    const [fieldSelections, setFieldSelections] = useState({});
    const [showFieldSelector, setShowFieldSelector] = useState(null); // recordId being customized
    const [showGlobalFieldSelector, setShowGlobalFieldSelector] = useState(false); // ✅ NEW: Global field selector
    const [globalFieldSelections, setGlobalFieldSelections] = useState({}); // ✅ NEW: Global selections
    // ✅ NEW: Inspect window state
    const [showInspect, setShowInspect] = useState(false);

    // Modal states for notifications
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null
    });

    const [resultModal, setResultModal] = useState({
        open: false,
        title: '',
        message: '',
        isError: false
    });

    // ✅ Helper to format numeric values with proper decimal places
    const formatNumericValue = (field, value) => {
        if (value === null || value === undefined || value === '') {
            return <em>(empty)</em>;
        }
        
        const num = parseFloat(value);
        if (isNaN(num)) {
            return String(value);
        }
        
        // GWP fields need 5 decimal places
        if (GWP_DIFF_FIELDS.includes(field)) {
            return num.toFixed(5);
        }

        // Other numeric fields - show up to 2 decimal places, remove trailing zeros
        return num.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    };

    // ✅ Helper to format values as STRINGS (for inspect table - no JSX)
    const formatChangeValueString = (field, value) => {
        // Format date fields as DD/MM/YYYY
        if (['Data_valid_start', 'Data_valid_end'].includes(field)) {
            if (!value) return '(empty)';
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${day}/${month}/${year}`;
                }
            } catch (error) {
                // Fall through to default
            }
        }
        
        // ✅ Format numeric fields with proper precision
        if (NUMERIC_DIFF_FIELDS.includes(field)) {
            if (value === null || value === undefined || value === '') {
                return '(empty)';
            }

            const num = parseFloat(value);
            if (isNaN(num)) {
                return String(value);
            }

            // GWP fields need 5 decimal places
            if (GWP_DIFF_FIELDS.includes(field)) {
                return num.toFixed(5);
            }
            
            // Other numeric fields
            return num.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            });
        }
        
        // Default formatting
        return value === null || value === '' ? '(empty)' : String(value);
    };



    // ✅ Get field-friendly names
    const getFieldLabel = (fieldName) => {
        const labels = {
            'material_type': 'Material Type',
            'name': 'Name',
            'Data_valid_start': 'Start Date',
            'Data_valid_end': 'End Date',
            'Ref_link': 'Ref Link',
            'ef_match': 'EF Match',
            'location': 'Location',
            'unit': 'Unit',
            'density_kg_m3': 'Density (kg/m³)',
            'area_density_kg_m2': 'Area Density (kg/m²)',
            'mass_per_m_kg_m': 'Mass per m (kg/m)',
            'mass_per_unit_kg_unit': 'Mass per Unit (kg/unit)',
            'gwp_total_kg_co2e_quant': 'GWP Total',
            'gwp_fossil_kg_co2e_quant': 'GWP Fossil',
            'gwp_biogenic_kg_co2e_quant': 'GWP Biogenic',
            'gwp_luluc_kg_co2e_quant': 'GWP LULUC',
            'gwp_stored_kg_co2e_quant': 'GWP Stored',
            'ExternalRefType': 'External Ref Type',
            'epd_registration_number': 'EPD Reg. No.',
            'epd_version': 'EPD Version',
            'material_classification': 'Material Classification',
            'declared_unit': 'Declared Unit',
            'declared_gwp_total': 'GWP Total (declared)',
            'declared_gwp_fossil': 'GWP Fossil (declared)',
            'declared_gwp_biogenic': 'GWP Biogenic (declared)',
            'declared_gwp_luluc': 'GWP LULUC (declared)',
            'declared_gwp_stored': 'GWP Stored (declared)'
        };
        return labels[fieldName] || fieldName;
    };

    // ✅ NEW: Get all unique fields across all update records
    const getAllUniqueFields = () => {
        if (!analysis || !analysis.updateRecords) return [];
        
        const fieldsSet = new Set();
        analysis.updateRecords.forEach(record => {
            Object.keys(record.changes).forEach(field => fieldsSet.add(field));
        });
        
        return Array.from(fieldsSet).sort();
    };

    // ✅ NEW: Initialize global field selections
    const initializeGlobalFieldSelections = () => {
        const allFields = getAllUniqueFields();
        const selections = {};
        allFields.forEach(field => {
            selections[field] = true; // All selected by default
        });
        setGlobalFieldSelections(selections);
    };

    // ✅ NEW: Toggle global field selection
    const toggleGlobalFieldSelection = (field) => {
        setGlobalFieldSelections(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    // ✅ NEW: Toggle all global fields
    const toggleAllGlobalFields = () => {
        const allFields = getAllUniqueFields();
        const allSelected = allFields.every(field => globalFieldSelections[field]);
        
        const newSelections = {};
        allFields.forEach(field => {
            newSelections[field] = !allSelected;
        });
        
        setGlobalFieldSelections(newSelections);
    };

    // ✅ NEW: Apply global selections to all records
    const applyGlobalFieldSelections = () => {
        const newFieldSelections = {};
        
        analysis.updateRecords.forEach(record => {
            const recordSelections = {};
            Object.keys(record.changes).forEach(field => {
                // Use global selection if field exists, otherwise default to true
                recordSelections[field] = globalFieldSelections[field] !== undefined 
                    ? globalFieldSelections[field] 
                    : true;
            });
            newFieldSelections[record.id] = recordSelections;
        });
        
        setFieldSelections(newFieldSelections);
        setShowGlobalFieldSelector(false);
    };

    // ✅ NEW: Open global field selector
    const openGlobalFieldSelector = () => {
        initializeGlobalFieldSelections();
        setShowGlobalFieldSelector(true);
    };

    // ✅ Initialize field selections (all selected by default)
    const initializeFieldSelections = (record) => {
        if (!fieldSelections[record.id]) {
            const fields = {};
            Object.keys(record.changes).forEach(field => {
                fields[field] = true; // All fields selected by default
            });
            setFieldSelections(prev => ({
                ...prev,
                [record.id]: fields
            }));
        }
    };

    // ✅ Toggle field selection
    const toggleFieldSelection = (recordId, field) => {
        setFieldSelections(prev => ({
            ...prev,
            [recordId]: {
                ...prev[recordId],
                [field]: !prev[recordId]?.[field]
            }
        }));
    };

    // ✅ Toggle all fields for a record
    const toggleAllFields = (recordId, changes) => {
        const currentSelections = fieldSelections[recordId] || {};
        const allSelected = Object.keys(changes).every(field => currentSelections[field]);
        
        const newSelections = {};
        Object.keys(changes).forEach(field => {
            newSelections[field] = !allSelected;
        });
        
        setFieldSelections(prev => ({
            ...prev,
            [recordId]: newSelections
        }));
    };

    // ✅ Open field selector modal
    const openFieldSelector = (record) => {
        initializeFieldSelections(record);
        setShowFieldSelector(record.id);
    };

    // ✅ Apply field selections (filter changes)
    const applyFieldSelections = () => {
        setShowFieldSelector(null);
    };

    // ✅ Get filtered record with only selected fields
    const groupKeyFor = (data) => {
        const mt = data.material_type || '(no material type)';
        const du = normaliseUnitClient(data.declared_unit) || normaliseUnitClient(data.unit) || '';
        return `${mt}||${du}`;
    };

    // Fold the resolution step's answers into the row before it is sent. Because
    // unit, the property, its estimated flag and the note are all ordinary EF
    // fields, they ride the existing write path — no special-casing on the server.
    const applyUnitResolution = (data) => {
        if (!unitGroups || !unitGroups.length) return data;

        const key = groupKeyFor(data);
        const group = unitGroups.find((g) => g.key === key);
        const keeping = !!keepAsDeclared[key];

        // Keeping the declared unit: the nezo unit simply mirrors it. When that
        // unit can't back a MUR (tonne) the EPD still imports — the backend
        // reports it as "kept" rather than failed.
        const chosen = keeping ? (group?.declaredUnit || data.unit || '') : unitChoices[key];
        if (!chosen) return data;

        const out = { ...data, unit: chosen, __keepAsDeclared: keeping };
        const field = MASS_FIELD_FOR_UNIT[chosen];
        const typed = typedProps[data.Id];

        if (field && typed !== undefined && typed !== '') {
            out[field] = typed;
            out[ESTIMATED_FLAG_FOR[field]] = 1;      // marks it as a supplied figure
            const note = groupNotes[key];
            if (note) out.estimated_source_note = note;
        }
        return out;
    };


    const getFilteredRecord = (record) => {
        if (record.action !== 'update' || !fieldSelections[record.id]) {
            return record;
        }

        const selectedFields = fieldSelections[record.id];
        const filteredChanges = {};
        
        Object.keys(record.changes).forEach(field => {
            if (selectedFields[field]) {
                filteredChanges[field] = record.changes[field];
            }
        });

        // Only include fields that are selected in the data
        const filteredData = { ...record.data };
        Object.keys(record.changes).forEach(field => {
            if (!selectedFields[field]) {
                // Revert to old value (don't update this field)
                filteredData[field] = record.changes[field].old;
            }
        });

        return {
            ...record,
            changes: filteredChanges,
            data: applyUnitResolution(filteredData)
        };
    };

    // Which group a row belongs to — mirrors the backend's grouping key
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setAnalysis(null);
            setSelectedRecords([]);
            setFieldSelections({});
            setGlobalFieldSelections({});
        }
    };

    const handleAnalyze = async () => {
        if (!file) return;

        setAnalyzing(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('mode', mode);

            const response = await api.post('/nabers-import/analyze', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                setAnalysis(response.data.comparison);
                setLinkSummary(response.data.linkSummary || null);
                setLinkPlans(response.data.linkPlans || null);

                const groups = response.data.unitGroups || [];
                setUnitGroups(groups);

                // Seed each group's unit: whatever the sheet already says, else the
                // unit this material type usually uses. Only ever a default — a
                // suggestion is never applied without appearing in the dropdown.
                try {
                    const sugRes = await api.get('/nabers-ref-ef/unit-suggestions');
                    const sug = sugRes.data.suggestions || {};
                    setSuggestions(sug);
                    const seeded = {};
                    groups.forEach((g) => {
                        seeded[g.key] = g.sheetNezoUnit || sug[g.materialType]?.unit || '';
                    });
                    setUnitChoices(seeded);
                } catch (e) {
                    const seeded = {};
                    groups.forEach((g) => { seeded[g.key] = g.sheetNezoUnit || ''; });
                    setUnitChoices(seeded);
                }

                // "No conversion" is pre-ticked whenever the declared unit is
                // already a valid nezo unit — that case must cost no decisions.
                // Left unticked for tonne so keeping it is always deliberate.
                const keeps = {};
                groups.forEach((g) => {
                    keeps[g.key] = NEZO_UNIT_OPTIONS.includes(g.declaredUnit);
                });
                setKeepAsDeclared(keeps);

                setTypedProps({});
                setGroupNotes({});
                
                // Auto-select all new and update records
                const autoSelected = [
                    ...response.data.comparison.newRecords,
                    ...response.data.comparison.updateRecords
                ];
                setSelectedRecords(autoSelected);

                // Initialize field selections for all update records (all fields selected)
                const initialSelections = {};
                response.data.comparison.updateRecords.forEach(record => {
                    const fields = {};
                    Object.keys(record.changes).forEach(field => {
                        fields[field] = true;
                    });
                    initialSelections[record.id] = fields;
                });
                setFieldSelections(initialSelections);
            }
        } catch (error) {
            console.error('❌ Analysis error:', error);
            setResultModal({
                open: true,
                title: 'Analysis Failed',
                message: 'Failed to analyze file: ' + (error.response?.data?.message || error.message),
                isError: true
            });
        } finally {
            setAnalyzing(false);
        }
    };

    const handleImport = async () => {
        if (selectedRecords.length === 0) {
            setResultModal({
                open: true,
                title: 'No Records Selected',
                message: 'Please select at least one record to import.',
                isError: true
            });
            return;
        }

        // Show confirmation modal
        setConfirmModal({
            open: true,
            title: 'Confirm Import',
            message: `Are you sure you want to import ${selectedRecords.length} record(s)?\n\nThis will ${
                analysis.newRecords.filter(r => selectedRecords.includes(r)).length > 0 ? 'create new records and ' : ''
            }${
                analysis.updateRecords.filter(r => selectedRecords.includes(r)).length > 0 ? 'update existing records' : ''
            }.${
                linkSummary
                    ? `\n\nMode: ${mode === 'epd_mur_qu' ? 'EPD + MUR + Product material' : 'EPD + MUR'}.` +
                      `\nIt will also create up to ${linkSummary.murToCreate} MUR(s)` +
                      (mode === 'epd_mur_qu' ? ` and ${linkSummary.quToCreate} material(s)` : '') +
                      `. Existing MURs and materials are never modified.`
                    : ''
            }`,
            onConfirm: executeImport
        });
    };

    const executeImport = async () => {
        setConfirmModal({ ...confirmModal, open: false });
        setImporting(true);

        try {
            // ✅ Filter records to only include selected fields
            const filteredRecords = selectedRecords.map(record => getFilteredRecord(record));
            
            console.log('📤 Sending records for import (with field selections):', filteredRecords);
            
            const response = await api.post('/nabers-import/execute', {
                records: filteredRecords,
                mode,
                categoryId: mode !== 'epd' ? categoryId : undefined,
                quCategoryId: mode === 'epd_mur_qu' ? quCategoryId : undefined,
                quColorHex: mode === 'epd_mur_qu' ? quColorHex : undefined,
                includeZeroWeight,
            });

            console.log('📥 Import response:', response.data);

            if (response.data.success) {
                const { results } = response.data;
                
                let message = '';
                let details = [];
                
                if (results.created > 0) {
                    details.push(`✅ Created: ${results.created} record(s)`);
                }
                if (results.updated > 0) {
                    details.push(`🔄 Updated: ${results.updated} record(s)`);
                }
                if (results.failed > 0) {
                    details.push(`❌ Failed: ${results.failed} record(s)`);
                }

                if (results.links) {
                    const l = results.links;
                    if (l.created > 0) details.push(`🔗 Links created: ${l.created}`);
                    if (l.skipped > 0) details.push(`↷ Already linked: ${l.skipped}`);
                    if (l.excluded > 0) details.push(`⚠ Excluded from linking: ${l.excluded}`);
                    if (l.error > 0) details.push(`❌ Link failures: ${l.error}`);
                }

                message = details.join('\n');
                
                // Add error details if any
                if (results.failed > 0 && results.errors && results.errors.length > 0) {
                    message += '\n\nErrors:\n';
                    results.errors.slice(0, 5).forEach(err => {
                        message += `• Row ${err.rowNum}: ${err.error}\n`;
                    });
                    if (results.errors.length > 5) {
                        message += `... and ${results.errors.length - 5} more errors`;
                    }
                }
                
                setResultModal({
                    open: true,
                    title: results.failed > 0 ? 'Import Completed with Errors' : 'Import Successful',
                    message: message,
                    isError: results.failed > 0 && results.created === 0 && results.updated === 0
                });
                
                // Close import modal and refresh data if any records were successfully imported
                if (results.created > 0 || results.updated > 0) {
                    setTimeout(() => {
                        setResultModal({ ...resultModal, open: false });
                        onImportComplete();
                        onClose();
                    }, 3000);
                }
            }
        } catch (error) {
            console.error('❌ Import error:', error);
            console.error('Error response:', error.response?.data);
            
            const errorMsg = error.response?.data?.message || error.message;
            const errorDetails = error.response?.data?.error || '';
            
            setResultModal({
                open: true,
                title: 'Import Failed',
                message: `${errorMsg}\n\n${errorDetails}`,
                isError: true
            });
        } finally {
            setImporting(false);
        }
    };

    const toggleRecordSelection = (record) => {
        const isSelected = selectedRecords.some(r => 
            r.id === record.id || (r.data && r.data.Id === record.data.Id)
        );

        if (isSelected) {
            setSelectedRecords(selectedRecords.filter(r => 
                r.id !== record.id && (!r.data || r.data.Id !== record.data.Id)
            ));
        } else {
            setSelectedRecords([...selectedRecords, record]);
        }
    };

    const toggleSelectAll = (recordList) => {
        const allSelected = recordList.every(record =>
            selectedRecords.some(r => 
                r.id === record.id || (r.data && r.data.Id === record.data.Id)
            )
        );

        if (allSelected) {
            // Deselect all from this list
            setSelectedRecords(selectedRecords.filter(selected =>
                !recordList.some(record => 
                    selected.id === record.id || (selected.data && selected.data.Id === record.data.Id)
                )
            ));
        } else {
            // Select all from this list
            const newSelections = recordList.filter(record =>
                !selectedRecords.some(r => 
                    r.id === record.id || (r.data && r.data.Id === record.data.Id)
                )
            );
            setSelectedRecords([...selectedRecords, ...newSelections]);
        }
    };

    const toggleRowExpansion = (id) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedRows(newExpanded);
    };

    const isRecordSelected = (record) => {
        return selectedRecords.some(r => 
            r.id === record.id || (r.data && r.data.Id === record.data.Id)
        );
    };

    // ✅ Show selected field count
    const getSelectedFieldCount = (record) => {
        const selections = fieldSelections[record.id];
        if (!selections) return Object.keys(record.changes).length;
        
        return Object.values(selections).filter(Boolean).length;
    };

    const renderChanges = (changes, recordId) => {
        const selections = fieldSelections[recordId] || {};

        // ✅ Helper to format date for JSX display (can return JSX elements)
        const formatChangeValueJSX = (field, value) => {
            // Format date fields as DD/MM/YYYY
            if (['Data_valid_start', 'Data_valid_end'].includes(field)) {
                if (!value) return <em>(empty)</em>;
                try {
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        return `${day}/${month}/${year}`;
                    }
                } catch (error) {
                    // Fall through to default
                }
            }
            
            // ✅ Format numeric fields with proper precision
            if (NUMERIC_DIFF_FIELDS.includes(field)) {
                return formatNumericValue(field, value);
            }
            
            // Default formatting
            return value === null || value === '' ? <em>(empty)</em> : String(value);
        };
        
        return (
            <div className="changes-detail">
                {Object.entries(changes).map(([field, change]) => {
                    const isSelected = selections[field] !== false;
                    
                    return (
                        <div key={field} className={`change-item ${!isSelected ? 'change-disabled' : ''}`}>
                            <strong>{getFieldLabel(field)}:</strong>
                            <div className="change-values">
                                <span className="old-value">
                                    Old: {formatChangeValueJSX(field, change.old)}
                                </span>
                                <span className="arrow">→</span>
                                <span className="new-value">
                                    New: {formatChangeValueJSX(field, change.new)}
                                </span>
                            </div>
                            {!isSelected && <span className="skip-badge">SKIPPED</span>}
                        </div>
                    );
                })}
            </div>
        );
    };



    return (
        <>
            <div className="import-modal-overlay" onClick={onClose}>
                <div className="import-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="import-modal-header">
                        <h2>📊 Import NABERS Data</h2>
                        <button className="close-btn" onClick={onClose}>×</button>
                    </div>

                    <div className="import-modal-body">
                        {/* Step 1: File Upload */}
                        <div className="import-step">
                            <h3>Step 1: Select File</h3>
                            <div className="file-upload-section">
                                <input
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    onChange={handleFileChange}
                                    disabled={analyzing || importing}
                                />
                                {file && (
                                    <div className="file-info">
                                        Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
                                    </div>
                                )}
                                {/* What this import should create */}
                                <div className="import-mode">
                                    <div className="import-mode-title">What should this file create?</div>
                                    {[
                                        { v: 'epd', label: 'EPD records only', hint: 'Writes the NABERS reference rows. Nothing else is touched.' },
                                        { v: 'epd_mur', label: 'EPD + MUR', hint: 'Also creates the Co2/Biogenic/Weight rate record for rows that have none.' },
                                        { v: 'epd_mur_qu', label: 'EPD + MUR + Product material', hint: 'Also creates the CAD Product material and links it to the rate.' },
                                    ].map((opt) => (
                                        <label key={opt.v} className={mode === opt.v ? 'import-mode-opt active' : 'import-mode-opt'}>
                                            <input
                                                type="radio"
                                                name="import-mode"
                                                checked={mode === opt.v}
                                                onChange={() => { setMode(opt.v); setAnalysis(null); setLinkSummary(null); setLinkPlans(null); }}
                                                disabled={analyzing || importing}
                                            />
                                            <span>
                                                <strong>{opt.label}</strong>
                                                <em>{opt.hint}</em>
                                            </span>
                                        </label>
                                    ))}

                                    {mode !== 'epd' && (
                                        <div className="import-mode-inputs">
                                            <p className="import-mode-note">
                                                Existing MURs and materials are never modified — only missing ones are created.
                                                Optional columns <code>MUR Name</code>, <code>MUR Category</code>,
                                                <code>MUR Quantity Type</code>, <code>MUR Co2</code>, <code>MUR Biogenic</code>,
                                                <code>MUR Weight</code>{mode === 'epd_mur_qu' && <>, <code>QU Name</code>, <code>QU Material Type</code>, <code>QU Colour</code></>} override
                                                the values below on a per-row basis.
                                            </p>
                                            <div className="import-mode-row">
                                                <label>
                                                    Default EPD Category:
                                                    <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} disabled={analyzing || importing}>
                                                        <option value="">— Select category —</option>
                                                        {epdCategories.map((c) => (
                                                            <option key={c.Id} value={c.Id}>{c.Categories}</option>
                                                        ))}
                                                    </select>
                                                </label>
                                                {mode === 'epd_mur_qu' && (
                                                    <>
                                                        <label>
                                                            Default Material Type:
                                                            <select value={quCategoryId} onChange={(e) => setQuCategoryId(e.target.value)} disabled={analyzing || importing}>
                                                                <option value="">— Select material type —</option>
                                                                {materialTypeGroups.map((node) => (
                                                                    node.type === 'group' ? (
                                                                        <optgroup key={node.id} label={node.label}>
                                                                            {node.options.map((o) => (
                                                                                <option key={o.id} value={o.id}>{o.label}</option>
                                                                            ))}
                                                                        </optgroup>
                                                                    ) : (
                                                                        <option key={node.id} value={node.id}>{node.label}</option>
                                                                    )
                                                                ))}
                                                            </select>
                                                        </label>
                                                        <label>
                                                            Colour:
                                                            <input type="color" value={quColorHex} onChange={(e) => setQuColorHex(e.target.value)} disabled={analyzing || importing} />
                                                        </label>
                                                    </>
                                                )}
                                                <label className="import-mode-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={includeZeroWeight}
                                                        onChange={(e) => setIncludeZeroWeight(e.target.checked)}
                                                        disabled={analyzing || importing}
                                                    />
                                                    Include rows whose weight would be 0
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <button
                                    className="btn-analyze"
                                    onClick={handleAnalyze}
                                    disabled={!file || analyzing || importing || (mode !== 'epd' && !categoryId) || (mode === 'epd_mur_qu' && !quCategoryId)}
                                >
                                    {analyzing ? 'Analyzing...' : 'Analyze File'}
                                </button>
                            </div>
                        </div>

                        {/* Step 1b: choose the nezo unit for each group and fill any gaps */}
                        {analysis && unitGroups && unitGroups.length > 0 && (
                            <div className="import-step">
                                <h3>Step 2: Set Nezo Units &amp; Properties</h3>
                                <p className="unit-step-intro">
                                    A new EPD only tells us its <strong>declared unit</strong>. Choose what each group
                                    should become in nezo, and the carbon values are converted for you. Where the
                                    property needed for that conversion is missing, type it in — anything you supply
                                    by hand is recorded as an estimate, not as EPD data.
                                </p>

                                {unitGroups.map((g) => {
                                    const keeping = !!keepAsDeclared[g.key];
                                    // When keeping, the nezo unit mirrors the declared one
                                    const chosen = keeping ? g.declaredUnit : (unitChoices[g.key] || '');
                                    const opt = g.options.find((o) => o.nezoUnit === chosen);
                                    const sug = suggestions[g.materialType];
                                    const massField = MASS_FIELD_FOR_UNIT[chosen];
                                    const blockedRows = keeping ? [] : (opt ? opt.blockedRows : []);
                                    // A unit that can't back a MUR (tonne) — the EPD still imports
                                    const keptUnlinkable = keeping && !NEZO_UNIT_OPTIONS.includes(g.declaredUnit);
                                    const showTonneHint = !keeping && g.declaredUnit === 'tonne';

                                    return (
                                        <div key={g.key} className="unit-group">
                                            <div className="unit-group-head">
                                                <div>
                                                    <strong>{g.materialType}</strong>
                                                    <span className="unit-group-meta">
                                                        declared in <code>{g.declaredUnit || '(none)'}</code> · {g.rowCount} row(s)
                                                    </span>
                                                </div>
                                                <div className="unit-group-controls">
                                                    <label className="unit-keep-check">
                                                        <input
                                                            type="checkbox"
                                                            checked={keeping}
                                                            onChange={(e) => setKeepAsDeclared((p) => ({ ...p, [g.key]: e.target.checked }))}
                                                            disabled={importing}
                                                        />
                                                        Keep as <code>{g.declaredUnit || '(none)'}</code> — no conversion
                                                    </label>
                                                    <label className="unit-group-pick">
                                                        Convert to:
                                                        <select
                                                            value={keeping ? '' : chosen}
                                                            onChange={(e) => setUnitChoices((p) => ({ ...p, [g.key]: e.target.value }))}
                                                            disabled={importing || keeping}
                                                        >
                                                            <option value="">— Choose —</option>
                                                            {NEZO_UNIT_OPTIONS.map((u) => (
                                                                <option key={u} value={u}>
                                                                    {u}{u === 'kg' && g.declaredUnit === 'tonne' ? '  (no density needed)' : ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                </div>
                                            </div>

                                            {!keeping && sug && sug.unit && (
                                                <div className="unit-group-suggest">
                                                    Existing {g.materialType} records mostly use <strong>{sug.unit}</strong>
                                                    {' '}({sug.share}% of {sug.sampleSize}) — change it freely if this batch differs.
                                                </div>
                                            )}
                                            {!keeping && sug && !sug.unit && (
                                                <div className="unit-group-suggest muted">
                                                    Existing {g.materialType} records are split across units, so there is no
                                                    reliable default — please choose.
                                                </div>
                                            )}
                                            {showTonneHint && (
                                                <div className="unit-group-suggest">
                                                    Declared in tonnes — <strong>kg</strong> needs no density at all (it's just ÷1000).
                                                    m² or m³ will ask you for the missing property.
                                                </div>
                                            )}

                                            {/* Outcome in plain words, so nothing about the conversion is implicit */}
                                            {keeping && !keptUnlinkable && (
                                                <div className="unit-group-status all-ready">
                                                    ✓ {g.rowCount} row(s): kept as <strong>{g.declaredUnit}</strong>, values unchanged, MURs created.
                                                </div>
                                            )}
                                            {keptUnlinkable && (
                                                <div className="unit-group-status kept">
                                                    ⓘ {g.rowCount} row(s): kept as <strong>{g.declaredUnit}</strong>. The EPD records
                                                    import in full, but nothing can be measured in {g.declaredUnit}, so no MUR is
                                                    created — they'll show "No MUR" and you can convert them any time.
                                                </div>
                                            )}
                                            {!keeping && chosen && opt && (
                                                <div className={`unit-group-status ${opt.blocked ? 'has-blocked' : 'all-ready'}`}>
                                                    {opt.blocked === 0
                                                        ? `✓ All ${opt.ready} row(s) convert ${g.declaredUnit || '?'} → ${chosen}.`
                                                        : `⚠ ${opt.ready} row(s) ready · ${opt.blocked} need ${MASS_LABEL_FOR_UNIT[chosen] || 'a property'} before they can convert to ${chosen}. Leave one blank and that row imports without a MUR.`}
                                                </div>
                                            )}

                                            {chosen && massField && blockedRows.length > 0 && (
                                                <div className="unit-group-fill">
                                                    <div className="unit-group-fill-head">
                                                        Supply {MASS_LABEL_FOR_UNIT[chosen]} for these rows
                                                        <span className="unit-group-fill-hint">
                                                            left blank = imported without a MUR
                                                        </span>
                                                    </div>
                                                    <label className="unit-group-note">
                                                        Where did these figures come from?
                                                        <input
                                                            type="text"
                                                            placeholder="e.g. typical value, Boral technical data sheet"
                                                            value={groupNotes[g.key] || ''}
                                                            onChange={(e) => setGroupNotes((p) => ({ ...p, [g.key]: e.target.value }))}
                                                            disabled={importing}
                                                        />
                                                    </label>
                                                    <div className="unit-group-rows">
                                                        {blockedRows.map((r) => (
                                                            <div key={r.Id} className="unit-fill-row">
                                                                <span className="unit-fill-name" title={r.name}>{r.name || r.Id}</span>
                                                                <input
                                                                    type="number"
                                                                    step="0.0000001"
                                                                    placeholder={MASS_LABEL_FOR_UNIT[chosen]}
                                                                    value={typedProps[r.Id] ?? ''}
                                                                    onChange={(e) => setTypedProps((p) => ({ ...p, [r.Id]: e.target.value }))}
                                                                    disabled={importing}
                                                                />
                                                                {typedProps[r.Id] !== undefined && typedProps[r.Id] !== '' && (
                                                                    <span className="estimated-tag" title="Will be flagged as a manually supplied estimate">
                                                                        estimate
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Step 3: Analysis Results */}
                        {analysis && (
                            <>
                                <div className="import-step">
                                    <h3>Step 3: Review Changes</h3>
                                    <div className="analysis-summary">
                                        <div className="summary-item new">
                                            <span className="summary-label">New Records:</span>
                                            <span className="summary-value">{analysis.summary.new}</span>
                                        </div>
                                        <div className="summary-item update">
                                            <span className="summary-label">Updates:</span>
                                            <span className="summary-value">{analysis.summary.updates}</span>
                                        </div>
                                        <div className="summary-item unchanged">
                                            <span className="summary-label">Unchanged:</span>
                                            <span className="summary-value">{analysis.summary.unchanged}</span>
                                        </div>
                                        <div className="summary-item error">
                                            <span className="summary-label">Errors:</span>
                                            <span className="summary-value">{analysis.summary.errors}</span>
                                        </div>
                                    </div>

                                    {linkSummary && (
                                        <div className="link-summary">
                                            <div className="link-summary-title">
                                                Links to be created ({mode === 'epd_mur_qu' ? 'MUR + Product material' : 'MUR only'})
                                            </div>
                                            <div className="analysis-summary">
                                                <div className="summary-item new">
                                                    <span className="summary-label">MURs to create:</span>
                                                    <span className="summary-value">{linkSummary.murToCreate}</span>
                                                </div>
                                                <div className="summary-item new">
                                                    <span className="summary-label">Materials to create:</span>
                                                    <span className="summary-value">{linkSummary.quToCreate}</span>
                                                </div>
                                                <div className="summary-item unchanged">
                                                    <span className="summary-label">Already linked:</span>
                                                    <span className="summary-value">{linkSummary.alreadyLinked}</span>
                                                </div>
                                                <div className="summary-item error">
                                                    <span className="summary-label">Excluded:</span>
                                                    <span className="summary-value">{linkSummary.excluded}</span>
                                                </div>
                                            </div>
                                            {linkPlans && linkPlans.some((p) => p.murStatus === 'excluded') && (
                                                <details className="link-excluded">
                                                    <summary>
                                                        Why {linkPlans.filter((p) => p.murStatus === 'excluded').length} row(s) are excluded
                                                    </summary>
                                                    <ul>
                                                        {linkPlans.filter((p) => p.murStatus === 'excluded').slice(0, 30).map((p) => (
                                                            <li key={p.Id}>
                                                                <strong>{p.name || p.Id}</strong> — {(p.problems || []).join('; ') || 'unit not mappable'}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </details>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* New Records */}
                                {analysis.newRecords.length > 0 && (
                                    <div className="records-section">
                                        <div className="section-header">
                                            <h4>🆕 New Records ({analysis.newRecords.length})</h4>
                                            <button
                                                className="btn-select-all"
                                                onClick={() => toggleSelectAll(analysis.newRecords)}
                                            >
                                                {analysis.newRecords.every(r => isRecordSelected(r)) ? 'Deselect All' : 'Select All'}
                                            </button>
                                        </div>
                                        <div className="records-list">
                                            {analysis.newRecords.map((record, idx) => (
                                                <div key={idx} className="record-item new-record">
                                                    <label className="record-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={isRecordSelected(record)}
                                                            onChange={() => toggleRecordSelection(record)}
                                                        />
                                                        <span className="record-summary">
                                                            <strong>Row {record.rowNum}:</strong> {record.data.name || record.data.Id} 
                                                            <span className="record-type"> ({record.data.material_type})</span>
                                                        </span>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Update Records */}
                                {analysis.updateRecords.length > 0 && (
                                    <div className="records-section">
                                        <div className="section-header">
                                            <h4>🔄 Updates ({analysis.updateRecords.length})</h4>
                                            <div className="section-header-actions">
                                                {/* ✅ NEW: Customize All Button */}
                                                <button
                                                    className="btn-customize-all"
                                                    onClick={openGlobalFieldSelector}
                                                    title="Customize fields for all update records"
                                                >
                                                    ⚙️ Customize All Fields
                                                </button>
                                                <button
                                                    className="btn-select-all"
                                                    onClick={() => toggleSelectAll(analysis.updateRecords)}
                                                >
                                                    {analysis.updateRecords.every(r => isRecordSelected(r)) ? 'Deselect All' : 'Select All'}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="records-list">
                                            {analysis.updateRecords.map((record, idx) => {
                                                const isExpanded = expandedRows.has(record.id);
                                                const selectedFields = getSelectedFieldCount(record);
                                                const totalFields = Object.keys(record.changes).length;
                                                
                                                return (
                                                    <div key={idx} className="record-item update-record">
                                                        <div className="record-header">
                                                            <label className="record-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isRecordSelected(record)}
                                                                    onChange={() => toggleRecordSelection(record)}
                                                                />
                                                                <span className="record-summary">
                                                                    <strong>Row {record.rowNum}:</strong> {record.data.name || record.id}
                                                                    <span className="change-count"> ({selectedFields}/{totalFields} fields)</span>
                                                                </span>
                                                            </label>
                                                            <div className="record-actions">
                                                                <button
                                                                    className="btn-customize"
                                                                    onClick={() => openFieldSelector(record)}
                                                                    title="Customize which fields to update"
                                                                >
                                                                    ⚙️ Customize
                                                                </button>
                                                                <button
                                                                    className="btn-expand"
                                                                    onClick={() => toggleRowExpansion(record.id)}
                                                                >
                                                                    {isExpanded ? '▼' : '▶'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {isExpanded && renderChanges(record.changes, record.id)}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Errors */}
                                {analysis.errors.length > 0 && (
                                    <div className="records-section">
                                        <h4 className="error-header">❌ Errors ({analysis.errors.length})</h4>
                                        <div className="records-list">
                                            {analysis.errors.map((error, idx) => (
                                                <div key={idx} className="record-item error-record">
                                                    <strong>Row {error.row}:</strong> {error.error}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Step 3: Import Actions */}
                    {analysis && (
                        <div className="import-modal-footer">
                            <div className="selected-count">
                                {selectedRecords.length} record(s) selected
                            </div>
                            <div className="footer-buttons">
                                <button className="btn-cancel" onClick={onClose}>
                                    Cancel
                                </button>
                                <button
                                    className="btn-import"
                                    onClick={handleImport}
                                    disabled={selectedRecords.length === 0 || importing}
                                >
                                    {importing ? 'Importing...' : `Import ${selectedRecords.length} Record(s)`}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ✅ ENHANCED: Global Field Selector Modal with Inspect Window */}
            {showGlobalFieldSelector && (() => {
                const allFields = getAllUniqueFields();
                const allSelected = allFields.every(field => globalFieldSelections[field]);
                
                return (
                    <div className="notification-modal-overlay" onClick={() => setShowGlobalFieldSelector(false)}>
                        <div className="field-selector-modal global-field-selector" onClick={(e) => e.stopPropagation()}>
                            <h3>⚙️ Customize Fields for All Records</h3>
                            <p className="field-selector-subtitle">
                                Select which fields to update across all {analysis.updateRecords.length} record(s)
                            </p>
                            
                            <div className="field-selector-header">
                                <button
                                    className="btn-toggle-all"
                                    onClick={toggleAllGlobalFields}
                                >
                                    {allSelected ? '❌ Deselect All' : '✅ Select All'}
                                </button>
                                {/* ✅ NEW: Inspect Button */}
                                <button
                                    className="btn-inspect"
                                    onClick={() => setShowInspect(!showInspect)}
                                >
                                    {showInspect ? '📋 Hide Preview' : '🔍 Inspect Data'}
                                </button>
                            </div>
                            
                            {/* ✅ NEW: Inspect Window */}
                            {showInspect && (
                                <div className="inspect-window">
                                    <div className="inspect-header">
                                        <h4>Data Preview ({analysis.updateRecords.length} records)</h4>
                                        <p className="inspect-subtitle">
                                            Click on column headers to toggle which fields to update
                                        </p>
                                    </div>
                                    <div className="inspect-table-container">
                                        <table className="inspect-table">
                                            <thead>
                                                <tr>
                                                    <th className="sticky-col">Row</th>
                                                    <th className="sticky-col">Name</th>
                                                    {allFields.map(field => (
                                                        <th 
                                                            key={field}
                                                            className={`clickable-header ${globalFieldSelections[field] === false ? 'disabled-column' : 'enabled-column'}`}
                                                            onClick={() => toggleGlobalFieldSelection(field)}
                                                            title={`Click to ${globalFieldSelections[field] === false ? 'enable' : 'disable'} this field`}
                                                        >
                                                            <div className="column-header">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={globalFieldSelections[field] !== false}
                                                                    onChange={() => {}}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                                <span>{getFieldLabel(field)}</span>
                                                            </div>
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {analysis.updateRecords.map((record, idx) => (
                                                    <tr key={record.id}>
                                                        <td className="sticky-col row-number">{record.rowNum}</td>
                                                        <td className="sticky-col name-cell" title={record.data.name}>
                                                            {record.data.name || record.id}
                                                        </td>
                                                        {allFields.map(field => {
                                                            const change = record.changes[field];
                                                            const isEnabled = globalFieldSelections[field] !== false;
                                                            
                                                            if (!change) {
                                                                return (
                                                                    <td key={field} className="no-change-cell">
                                                                        -
                                                                    </td>
                                                                );
                                                            }
                                                            
                                                            return (
                                                                <td 
                                                                    key={field} 
                                                                    className={`change-cell ${isEnabled ? '' : 'disabled-cell'}`}
                                                                    title={`Old: ${change.old || '(empty)'} → New: ${change.new || '(empty)'}`}
                                                                >
                                                                    <div className="cell-content">
                                                                        <span className="old-value-cell">{formatChangeValueString(field, change.old)}</span>
                                                                        <span className="arrow-cell">→</span>
                                                                        <span className="new-value-cell">{formatChangeValueString(field, change.new)}</span>
                                                                    </div>
                                                                    {!isEnabled && <div className="disabled-overlay">SKIP</div>}
                                                                </td>
                                                            );
                                                        })}

                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="inspect-legend">
                                        <div className="legend-item">
                                            <span className="legend-color enabled-legend"></span>
                                            <span>Enabled - Will update</span>
                                        </div>
                                        <div className="legend-item">
                                            <span className="legend-color disabled-legend"></span>
                                            <span>Disabled - Will skip</span>
                                        </div>
                                        <div className="legend-item">
                                            <span className="legend-color nochange-legend"></span>
                                            <span>No change</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="field-selector-list">
                                {allFields.map(field => (
                                    <label key={field} className="field-selector-item">
                                        <input
                                            type="checkbox"
                                            checked={globalFieldSelections[field] !== false}
                                            onChange={() => toggleGlobalFieldSelection(field)}
                                        />
                                        <div className="field-info">
                                            <strong>{getFieldLabel(field)}</strong>
                                            <div className="field-description">
                                                Will apply to all records with this field
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                            
                            <div className="field-selector-footer">
                                <button
                                    className="btn-cancel-small"
                                    onClick={() => setShowGlobalFieldSelector(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn-apply"
                                    onClick={applyGlobalFieldSelections}
                                >
                                    Apply to All Records
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Field Selector Modal (Single Record) */}
            {showFieldSelector && (() => {
                const record = analysis.updateRecords.find(r => r.id === showFieldSelector);
                if (!record) return null;
                
                const selections = fieldSelections[record.id] || {};
                const allSelected = Object.keys(record.changes).every(field => selections[field]);
                
                return (
                    <div className="notification-modal-overlay" onClick={() => setShowFieldSelector(null)}>
                        <div className="field-selector-modal" onClick={(e) => e.stopPropagation()}>
                            <h3>⚙️ Customize Fields to Update</h3>
                            <p className="field-selector-subtitle">
                                Row {record.rowNum}: {record.data.name || record.id}
                            </p>
                            
                            <div className="field-selector-header">
                                <button
                                    className="btn-toggle-all"
                                    onClick={() => toggleAllFields(record.id, record.changes)}
                                >
                                    {allSelected ? '❌ Deselect All' : '✅ Select All'}
                                </button>
                            </div>
                            
                            <div className="field-selector-list">
                                {Object.entries(record.changes).map(([field, change]) => {
                                    // ✅ Helper to format date values in field selector
                                    const formatFieldValue = (value) => {
                                            return formatChangeValueString(field, value);
                                        };

                                    return (
                                        <label key={field} className="field-selector-item">
                                            <input
                                                type="checkbox"
                                                checked={selections[field] !== false}
                                                onChange={() => toggleFieldSelection(record.id, field)}
                                            />
                                            <div className="field-info">
                                                <strong>{getFieldLabel(field)}</strong>
                                                <div className="field-change-preview">
                                                    <span className="old">{formatFieldValue(change.old)}</span>
                                                    <span className="arrow">→</span>
                                                    <span className="new">{formatFieldValue(change.new)}</span>
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                            
                            <div className="field-selector-footer">
                                <button
                                    className="btn-apply"
                                    onClick={applyFieldSelections}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Confirmation Modal */}
            {confirmModal.open && (
                <div className="notification-modal-overlay" onClick={() => setConfirmModal({ ...confirmModal, open: false })}>
                    <div className="notification-modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="notification-icon">⚠️</div>
                        <h2 className="notification-title">{confirmModal.title}</h2>
                        <p className="notification-message">{confirmModal.message}</p>
                        <div className="notification-buttons">
                            <button 
                                className="notification-btn cancel-btn" 
                                onClick={() => setConfirmModal({ ...confirmModal, open: false })}
                            >
                                Cancel
                            </button>
                            <button 
                                className="notification-btn confirm-btn" 
                                onClick={confirmModal.onConfirm}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Result Modal (Success/Error) */}
            {resultModal.open && (
                <div className="notification-modal-overlay" onClick={() => setResultModal({ ...resultModal, open: false })}>
                    <div 
                        className={`notification-modal-content ${resultModal.isError ? 'error-notification' : 'success-notification'}`} 
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="notification-icon">{resultModal.isError ? '❌' : '✅'}</div>
                        <h2 className="notification-title">{resultModal.title}</h2>
                        <p className="notification-message" style={{ whiteSpace: 'pre-line' }}>{resultModal.message}</p>
                        <button 
                            className={`notification-btn ${resultModal.isError ? 'error-btn' : 'success-btn'}`} 
                            onClick={() => setResultModal({ ...resultModal, open: false })}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default NabersImportModal;
